import { state } from '../state.js';
import { formatUptime } from '../utils/formatters.js';
import { showToast } from '../utils/toast.js';
import { fetchConnections } from './connections.controller.js';
import { fetchBatchInitialData } from './batch.controller.js';
import { initExplorer } from './explorer.controller.js';
import { initStaging } from './staging.controller.js';
import { initComparison } from './comparison.controller.js';
import { fetchJobs, fetchAuditLogs, startLogsLivePolling, stopLogsLivePolling } from './logs.controller.js';
import { fetchMonitoringData, startMonitoringLivePolling, stopMonitoringLivePolling } from './monitoring.controller.js';

/**
 * Check if the dashboard is running inside an iframe (e.g., Akeneo PIM App iframe)
 */
export function detectIframeMode(): void {
  const urlParams = new URLSearchParams(window.location.search);
  const forceEmbed =
    urlParams.get('embedded') === 'true' ||
    urlParams.get('embedded') === '1' ||
    urlParams.get('iframe') === 'true';
  let inIframe = false;

  try {
    inIframe = window.self !== window.top;
  } catch {
    inIframe = true;
  }

  // Safety check: if window.top === window.self, we are in top-level window
  if (typeof window !== 'undefined' && window.top === window.self) {
    inIframe = false;
  }

  state.isEmbedded = forceEmbed || inIframe;
  applyEmbeddingUI();
}

/**
 * Apply styling and indicators for standalone vs embedded modes
 */
export function applyEmbeddingUI(): void {
  const modeBadge = document.getElementById('mode-badge');
  const modeIcon = document.getElementById('mode-icon');
  const modeText = document.getElementById('mode-text');
  const footerToggle = document.getElementById('footer-embed-toggle');

  if (state.isEmbedded) {
    document.body.classList.add('is-embedded');
    if (modeBadge) modeBadge.title = 'Running Embedded in Akeneo PIM';
    if (modeIcon) modeIcon.textContent = '📦';
    if (modeText) modeText.textContent = 'Akeneo Frame';
    if (footerToggle) footerToggle.textContent = 'Switch to Standalone View';
  } else {
    document.body.classList.remove('is-embedded');
    if (modeBadge) modeBadge.title = 'Running Standalone Browser Mode';
    if (modeIcon) modeIcon.textContent = '🌐';
    if (modeText) modeText.textContent = 'Standalone';
    if (footerToggle) footerToggle.textContent = 'Switch to Embedded View';
  }
}

/**
 * Toggle embedding mode manually (useful for testing and previews)
 */
export function toggleEmbeddingMode(): void {
  state.isEmbedded = !state.isEmbedded;
  applyEmbeddingUI();
  showToast(
    state.isEmbedded ? 'Switched to Embedded (Akeneo Frame) Mode' : 'Switched to Standalone Browser Mode',
    'info'
  );
}

/**
 * Client-Side Tab Switching & Hash Navigation
 */
export function switchTab(tabId: string, updateHash: boolean = true): void {
  const rawTabId = tabId;
  const queryIndex = tabId.indexOf('?');
  if (queryIndex > -1) {
    tabId = tabId.slice(0, queryIndex);
  }

  const validTabs = ['overview', 'connections', 'batch', 'explorer', 'staging', 'logs', 'monitoring', 'compare'];
  if (!validTabs.includes(tabId)) {
    tabId = 'overview';
  }

  state.activeTab = tabId;

  // Update tab buttons
  document.querySelectorAll('.tab-btn').forEach((btn) => {
    const isTarget = btn.getAttribute('data-tab') === tabId;
    btn.classList.toggle('active', isTarget);
    btn.setAttribute('aria-selected', isTarget ? 'true' : 'false');
  });

  // Update tab panels
  document.querySelectorAll('.tab-panel').forEach((panel) => {
    const isTarget = panel.id === `tab-${tabId}`;
    panel.classList.toggle('active', isTarget);
  });

  if (updateHash) {
    let currentHash = '';
    let locationExists = false;
    try {
      locationExists = typeof window !== 'undefined' && !!window.location;
      if (locationExists) currentHash = window.location.hash;
    } catch {
      locationExists = false;
    }
    if (locationExists) {
      let targetHash = `#${tabId}`;
      if (tabId === 'compare' && rawTabId.includes('?')) {
        targetHash = `#${rawTabId}`;
      }
      if (currentHash !== targetHash) {
        window.location.hash = targetHash;
      }
    }
  }

  // Refresh data on specific tabs
  if (tabId === 'connections') {
    fetchConnections();
  } else if (tabId === 'batch') {
    fetchBatchInitialData();
  } else if (tabId === 'explorer') {
    initExplorer();
  } else if (tabId === 'staging') {
    initStaging();
  } else if (tabId === 'compare') {
    initComparison();
  } else if (tabId === 'logs') {
    fetchJobs();
    fetchAuditLogs();
    startLogsLivePolling();
  } else if (tabId === 'monitoring') {
    fetchMonitoringData();
    startMonitoringLivePolling();
  }

  if (tabId !== 'logs') {
    stopLogsLivePolling();
  }
  if (tabId !== 'monitoring') {
    stopMonitoringLivePolling();
  }
}

/**
 * Initialize Hash Navigation listener
 */
export function initRouter(): void {
  function handleHash(): void {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      switchTab(hash, false);
    } else {
      switchTab('overview', false);
    }
  }

  window.addEventListener('hashchange', handleHash);
  handleHash();
}

/**
 * Fetch and update Server API Health
 */
export async function fetchHealth(): Promise<void> {
  const statusIndicator = document.getElementById('status-indicator');
  const statusText = document.getElementById('status-text');
  const statusLatency = document.getElementById('status-latency');
  const badgeStatus = document.getElementById('badge-status');
  const metricHealth = document.getElementById('metric-health');
  const metricUptime = document.getElementById('metric-uptime');
  const metricVersion = document.getElementById('metric-version');
  const navVersionBadge = document.getElementById('nav-version-badge');
  const metricPing = document.getElementById('metric-ping');

  const startTime = performance.now();

  try {
    const res = await fetch('/api/v1/health');
    const latency = Math.round(performance.now() - startTime);

    if (!res.ok) {
      throw new Error(`HTTP error ${res.status}`);
    }

    const data = await res.json();
    state.health = {
      status: data.status || 'healthy',
      uptime: data.uptime || 0,
      version: data.version || '0.2.0',
      latency,
    };

    if (statusIndicator) {
      statusIndicator.className = 'status-indicator online';
    }
    if (statusText) statusText.textContent = 'Online';
    if (statusLatency) statusLatency.textContent = `${latency}ms`;
    if (badgeStatus) {
      badgeStatus.textContent = 'Healthy';
      badgeStatus.className = 'badge badge-success';
    }
    if (metricHealth) metricHealth.textContent = state.health.status;
    if (metricUptime) metricUptime.textContent = formatUptime(state.health.uptime);
    if (metricVersion) metricVersion.textContent = state.health.version;
    if (navVersionBadge) navVersionBadge.textContent = `v${state.health.version}`;
    if (metricPing) metricPing.textContent = `${latency} ms`;
  } catch {
    state.health.status = 'offline';
    state.health.latency = null;

    if (statusIndicator) {
      statusIndicator.className = 'status-indicator offline';
    }
    if (statusText) statusText.textContent = 'Offline';
    if (statusLatency) statusLatency.textContent = 'error';
    if (badgeStatus) {
      badgeStatus.textContent = 'Offline';
      badgeStatus.className = 'badge badge-danger';
    }
    if (metricHealth) metricHealth.textContent = 'Unavailable';
    if (metricPing) metricPing.textContent = 'Failed';
  }
}

/**
 * Check URL query parameters for connection notifications (e.g. from OAuth redirect)
 */
export function checkUrlNotifications(): void {
  const params = new URLSearchParams(window.location.search);
  if (params.get('status') === 'connected') {
    const connId = params.get('connection_id') || 'Akeneo';
    showToast(`Successfully connected and authenticated: ${connId}`, 'success', 5000);
    switchTab('connections');
  }
}
